import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpCacheManager } from '@ngneat/cashew';

import { environment } from '@ers-cat-app/env/environment';
import { LoginRequest } from '@ers/shared';
import { ROUTE_URLS, STORAGE_KEYS } from '@ers-cat-app/shared/constants';
import { Role } from '@ers/shared';
import { Router, ActivatedRoute } from '@angular/router';
import { THEME } from '../system/system.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly httpCacheManager = inject(HttpCacheManager, {
    optional: true,
  });

  private readonly BASE_URL = `${environment.apiUrl}/auth`;

  readonly isLoggedIn$ = new BehaviorSubject<boolean>(false);
  readonly accessToken$ = new BehaviorSubject<string | null>(null);
  accessToken: string | null;
  jwtHelper: JwtHelperService = new JwtHelperService();

  constructor() {
    const storedToken =
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    this.accessToken = this.isAppAccessToken(storedToken) ? storedToken : null;

    if (
      (!this.accessToken || this.accessToken?.length === 0) &&
      this.isLoggedIn$.value
    ) {
      this.logout();
    }

    this.accessToken$.next(this.accessToken);
    this.isLoggedIn$.next(!!this.accessToken);
  }

  get isDpsWorkOrderUser() {
    const isDpsWorkOrderUser = this.userHasRole(Role.DPS_WORK_ORDER);
    const userHasOnlyOneRole = this.getRoles().length === 1;

    return isDpsWorkOrderUser && userHasOnlyOneRole;
  }

  login(
    user: LoginRequest,
    isNormalLogin: boolean = true,
  ): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.BASE_URL}/login`, user).pipe(
      switchMap((response: LoginResponse): Observable<LoginResponse> => {
        if (isNormalLogin) {
          this.onLoginSuccess(response, true);
        }
        return of(response);
      }),
    );
  }

  async logout() {
    const selectedTheme = localStorage.getItem(STORAGE_KEYS.THEME);
    this.removeStorageKeys();
    const user = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);

    // Clear local storage
    localStorage.clear();
    sessionStorage.clear();

    // Set theme again
    localStorage.setItem(STORAGE_KEYS.THEME, selectedTheme as THEME);

    // Set User Email (for Save User ID)
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, user);
    }

    // set theme
    if (selectedTheme) {
      localStorage.setItem(STORAGE_KEYS.THEME, selectedTheme);
    }

    this.accessToken = null;
    this.accessToken$.next(null);
    this.isLoggedIn$.next(false);
  }

  /** @deprecated Client requested to let azure handle the logout. */
  async logoutWithAzure() {
    const user = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
    const selectedTheme = localStorage.getItem(STORAGE_KEYS.THEME);

    localStorage.clear();
    sessionStorage.clear();
    if (user) {
      localStorage.setItem(STORAGE_KEYS.USER_EMAIL, user);
    }
    // await this.msalService.logout();

    if (selectedTheme) {
      localStorage.setItem(STORAGE_KEYS.THEME, selectedTheme);
    }
  }

  handleRedirectAfterLogin() {
    // Check if next url exists (for unauthorized users who click a link that requires login)
    const nextUrl = this.getNextUrl();
    const hasNextUrl = nextUrl && nextUrl !== '/';

    const isSafeUrl = (url: string): boolean => {
      if (!url) {
        return false;
      }

      // Relative URL (does not start with http/https or www)
      if (!/^https?:\/\//i.test(url) && !/^www\./i.test(url)) {
        return true;
      }

      // Check domain using isErsDomain
      if (this.isErsDomain(url)) {
        return true;
      }

      // Check if url starts with %2F followed by a valid route
      try {
        const routeValues = Object.values(ROUTE_URLS);
        for (const route of routeValues) {
          if (url.startsWith(`%2F${route}`)) {
            return true;
          }
        }
      } catch {}
      return false;
    };

    if (this.isDpsWorkOrderUser) {
      this.router.navigate([ROUTE_URLS.DPS_WORK_ORDER]);
    } else if (hasNextUrl && isSafeUrl(nextUrl)) {
      // Clean up stored next param
      localStorage.removeItem(STORAGE_KEYS.NEXT);
      sessionStorage.removeItem(STORAGE_KEYS.NEXT);

      // Parse parameters and use queryParams or matrix params for navigation
      let path = nextUrl;
      const queryParams: Record<string, string> = {};
      const matrixParams: Record<string, string> = {};

      // Handle ?key=value query string
      if (nextUrl.includes('?')) {
        const [basePath, queryString] = nextUrl.split('?');
        path = basePath;
        const searchParams = new URLSearchParams(queryString);
        searchParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      // Handle ;key=value matrix params
      const matrixParts = path.split(';');
      path = matrixParts[0];
      matrixParts.slice(1).forEach(part => {
        const [key, value] = part.split('=', 2);
        if (key) {
          matrixParams[key] = value === undefined ? 'true' : value;
        }
      });

      if (
        Object.keys(matrixParams).length > 0 &&
        Object.keys(queryParams).length > 0
      ) {
        this.router.navigate([path, matrixParams], { queryParams });
      } else if (Object.keys(matrixParams).length > 0) {
        this.router.navigate([path, matrixParams]);
      } else if (Object.keys(queryParams).length > 0) {
        this.router.navigate([path], { queryParams });
      } else {
        this.router.navigate([path]);
      }
    } else {
      this.router.navigate([ROUTE_URLS.HOME]);
    }
  }

  removeStorageKeys() {
    const localStorageKeys = [
      STORAGE_KEYS.EQUIPMENT_CACHE,
      STORAGE_KEYS.LOGGED_IN,
      STORAGE_KEYS.TOKEN_EXPIRATION,
      STORAGE_KEYS.ACCESS_TOKEN,
      STORAGE_KEYS.STATIC_VARS_CACHE,
      STORAGE_KEYS.NEXT, // was stored in local for login redirect backup
    ];
    const sessionStorageKeys = [
      STORAGE_KEYS.ACCESS_TOKEN,
      STORAGE_KEYS.LOGGED_IN,
      STORAGE_KEYS.TOKEN_EXPIRATION,
    ];

    sessionStorageKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

    localStorageKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.BASE_URL}/forgot-password`, {
      email: email,
    });
  }

  onLoginSuccess(response: { accessToken: string }, rememberMe: boolean) {
    this.accessToken = response.accessToken;
    this.accessToken$.next(response.accessToken);
    const decodedUser = this.jwtHelper.decodeToken(response.accessToken);

    localStorage.removeItem(STORAGE_KEYS.EQUIPMENT_CACHE);
    localStorage.removeItem(STORAGE_KEYS.STATIC_VARS_CACHE);
    this.httpCacheManager?.clear();

    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LOGGED_IN);
    sessionStorage.removeItem(STORAGE_KEYS.LOGGED_IN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION);

    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
      localStorage.setItem(STORAGE_KEYS.LOGGED_IN, 'true');
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, decodedUser.exp);
    } else {
      sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
      sessionStorage.setItem(STORAGE_KEYS.LOGGED_IN, 'true');
      sessionStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, decodedUser.exp);
    }

    this.isLoggedIn$.next(true);

    this.handleRedirectAfterLogin();
  }

  resetPassword(code: string, password: string) {
    return this.http.post<boolean>(`${this.BASE_URL}/reset-password`, {
      code,
      password,
    });
  }

  getRequestAccessToken(): string {
    return this.isAppAccessToken(this.accessToken) ? this.accessToken || '' : '';
  }

  clearAccessToken() {
    this.accessToken = null;
    this.accessToken$.next(null);
    this.isLoggedIn$.next(false);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LOGGED_IN);
    sessionStorage.removeItem(STORAGE_KEYS.LOGGED_IN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION);
  }

  isAppAccessToken(accessToken = this.accessToken): boolean {
    try {
      if (!accessToken) {
        return false;
      }

      const payload = this.jwtHelper.decodeToken(accessToken);
      return payload?.userId !== undefined && payload?.userId !== null;
    } catch {
      return false;
    }
  }

  getEmailFromToken(accessToken = this.accessToken): string | null {
    try {
      if (!accessToken) {
        return null;
      }

      return this.jwtHelper.decodeToken(accessToken)?.email || null;
    } catch {
      return null;
    }
  }

  getUserIdFromToken(accessToken = this.accessToken): number | null {
    try {
      if (!accessToken) {
        return null;
      }

      const userId = this.jwtHelper.decodeToken(accessToken)?.userId;
      return userId === undefined || userId === null ? null : userId;
    } catch {
      return null;
    }
  }

  // By default, use current logged in user's info
  isAzureUser(accessToken = this.accessToken): boolean {
    try {
      if (!accessToken) {
        return false;
      }

      return this.jwtHelper.decodeToken(accessToken).azureIdExists;
    } catch (err) {
      return false;
    }
  }

  // By default, use current logged in user's info
  getRoles(accessToken = this.accessToken): Role[] {
    try {
      if (!accessToken) {
        return [];
      }

      return (this.jwtHelper.decodeToken(accessToken).roles as Role[]) || [];
    } catch (err) {
      return [];
    }
  }

  // By default, use current logged in user's info
  userHasRole(role: Role, accessToken = this.accessToken): boolean {
    const currentUserRoles = this.getRoles(accessToken).map(currRole =>
      currRole?.toUpperCase(),
    );
    return currentUserRoles.includes(role);
  }

  // By default, use current logged in user's info
  userHasRoleGivenListOfRoles(
    authorizedRoles: Role[],
    accessToken = this.accessToken,
  ): boolean {
    const currentUserRoles = this.getRoles(accessToken).map(role =>
      role.toUpperCase(),
    );
    return authorizedRoles.some((authorizedRole: Role) =>
      currentUserRoles.includes(authorizedRole),
    );
  }

  hasSalesHubAccess() {
    return this.userHasRoleGivenListOfRoles([
      Role.SALES_REP,
      Role.EXECUTIVE,
      Role.ADMIN,
      Role.MANAGER,
      Role.SALES_HUB,
      Role.SALES_HUB_ADMIN,
      Role.SALES_HUB_MANAGER,
    ]);
  }

  hasAdminSectionAccess() {
    return this.userHasRoleGivenListOfRoles([
      Role.ADMIN,
      Role.EXECUTIVE,
      Role.ADMIN_ROLES,
      Role.ADMIN_SYSTEMS,
      Role.ADMIN_USERS,
      Role.ADMIN_USERS_READ_ONLY,
    ]);
  }

  hasToolsSectionAccess() {
    return this.userHasRoleGivenListOfRoles([
      Role.CALCULATORS,
      Role.ON_DEMAND_DOCS,
      Role.OFF_RENT_REQUEST,
      Role.SAFETY_INCIDENT_REPORT,
      Role.UPLIFT,
      Role.HOUR_METER,
    ]);
  }

  hasAssetTrackingAccess() {
    return this.userHasRole(Role.ADMIN);
  }

  hasJobMonitoringAccess() {
    return this.userHasRole(Role.ADMIN);
  }

  hasNewsAccess() {
    return this.userHasRole(Role.NEWS);
  }

  // isAzureLoggedIn() {
  //   return this.msalService.isLoggedIn();
  // }

  private getNextUrl(): string {
    return (
      this.route?.snapshot?.queryParamMap?.get(STORAGE_KEYS.NEXT) ||
      localStorage.getItem(STORAGE_KEYS.NEXT) ||
      '/'
    );
  }

  private isErsDomain(url: string): boolean {
    const allowedDomains = (environment.ersDomainMatchString || '').split('|');
    return allowedDomains.some(domain => url.includes(domain));
  }
}

export type LoginResponse = { accessToken: string };
